import { useEffect, useRef } from "react";
import {
  useAudioVideo,
  useMeetingManager,
} from "amazon-chime-sdk-component-library-react";
import { DefaultBrowserBehavior } from "amazon-chime-sdk-js";
import { toast } from "sonner";
import { useDevices } from "../providers/DevicesProvider";
import { usePlatformStore } from "@platform/store";
import { logMeetingEvent } from "@core/features/videoMeeting/utils/logMeetingEvent";
import { isContinuityMicLabel } from "../utils/DeviceManager";
import {
  DeviceHotPlugToast,
  type DeviceType as HotPlugDeviceType,
} from "../components/DeviceHotPlugToast";

type PendingSwitch = {
  deviceType: HotPlugDeviceType;
  deviceLabel: string;
  revert?: () => Promise<void>;
};

function showHotPlugToast(args: {
  deviceType: HotPlugDeviceType;
  deviceLabel: string;
  duration: number;
  onUndo?: () => void | Promise<void>;
}) {
  toast.custom(
    (id) => (
      <DeviceHotPlugToast
        deviceType={args.deviceType}
        deviceLabel={args.deviceLabel}
        onUndo={
          args.onUndo
            ? async () => {
                await args.onUndo?.();
                toast.dismiss(id);
              }
            : undefined
        }
        onDismiss={() => toast.dismiss(id)}
      />
    ),
    {
      // Single id across all device types so simultaneous switches (e.g., a
      // Bluetooth I/O device firing both audio_input and audio_output in the
      // same effect run) collapse to one toast slot. Aggregation upstream
      // (see `flushPendingSwitches`) builds a unified message + Undo so the
      // single surviving toast carries all the necessary info.
      id: "device-hotplug",
      unstyled: true,
      className: "!bg-transparent !border-0 !shadow-none !p-0",
      duration: args.duration,
    },
  );
}

function flushPendingSwitches(pending: PendingSwitch[]) {
  if (pending.length === 0) return;

  const reverts = pending
    .map((s) => s.revert)
    .filter((r): r is () => Promise<void> => Boolean(r));
  const onUndo = reverts.length > 0
    ? async () => {
        for (const revert of reverts) {
          await revert();
        }
      }
    : undefined;
  const duration = onUndo ? 8000 : 5000;

  if (pending.length === 1) {
    const s = pending[0];
    showHotPlugToast({
      deviceType: s.deviceType,
      deviceLabel: s.deviceLabel,
      duration,
      onUndo,
    });
    return;
  }

  const allSameLabel = pending.every((s) => s.deviceLabel === pending[0].deviceLabel);
  if (allSameLabel) {
    // Common case: a Bluetooth I/O device (AirPods, headset) registers as
    // both an audioinput and an audiooutput, firing two switches with the
    // same label in the same effect run. Surface a single "audio" message
    // and a combined Undo that reverts both.
    showHotPlugToast({
      deviceType: "audio",
      deviceLabel: pending[0].deviceLabel,
      duration,
      onUndo,
    });
    return;
  }

  // Two or more switches to different labels in the same run — extremely
  // unusual (user simultaneously plugging in two distinct devices). Show
  // the most recent one; combined Undo still reverts every switch in the
  // batch so the user can roll back the whole event.
  const last = pending[pending.length - 1];
  showHotPlugToast({
    deviceType: last.deviceType,
    deviceLabel: last.deviceLabel,
    duration,
    onUndo,
  });
}

export function useDeviceHotPlug() {
  const {
    audioInputDevices,
    audioOutputDevices,
    videoInputDevices,
    selectedAudioInputDevice,
    selectedAudioOutputDevice,
    selectedVideoInputDevice,
    selectAudioInputDevice,
    selectAudioOutputDevice,
    selectVideoInputDevice,
  } = useDevices();

  const audioVideo = useAudioVideo();
  const meetingManager = useMeetingManager();

  const prevAudioInputRef = useRef<MediaDeviceInfo[]>([]);
  const prevAudioOutputRef = useRef<MediaDeviceInfo[]>([]);
  const prevVideoInputRef = useRef<MediaDeviceInfo[]>([]);
  const hasInitializedRef = useRef(false);

  const logDeviceEvent = (eventType: string, extra: Record<string, unknown>) => {
    const s = usePlatformStore.getState();
    logMeetingEvent({
      event_type: eventType,
      meeting_id: s.meetingId,
      room_id: s.roomId,
      user: s.user ? { id: s.user.id } : null,
      ...extra,
    });
  };

  useEffect(() => {
    if (!audioVideo) {
      hasInitializedRef.current = false;
      prevAudioInputRef.current = [];
      prevAudioOutputRef.current = [];
      prevVideoInputRef.current = [];
      return;
    }

    const timer = setTimeout(() => {
      if (!hasInitializedRef.current) {
        hasInitializedRef.current = true;
        prevAudioInputRef.current = audioInputDevices;
        prevAudioOutputRef.current = audioOutputDevices;
        prevVideoInputRef.current = videoInputDevices;
        return;
      }

      (async () => {
        const pendingSwitches: PendingSwitch[] = [];

        // --- Audio Input ---
        const prevAudioIn = prevAudioInputRef.current;
        const addedAudioIn = audioInputDevices.filter(
          (d) => !prevAudioIn.find((p) => p.deviceId === d.deviceId),
        );
        const removedAudioIn = prevAudioIn.filter(
          (p) => !audioInputDevices.find((d) => d.deviceId === p.deviceId),
        );
        const selectedAudioInDisappeared = removedAudioIn.some(
          (d) => d.deviceId === selectedAudioInputDevice,
        );

        if (selectedAudioInDisappeared && audioInputDevices.length > 0) {
          const fallback = audioInputDevices[0];
          try {
            await selectAudioInputDevice(fallback.deviceId);
            await meetingManager.startAudioInputDevice(fallback.deviceId);
            logDeviceEvent('device_hotplug_disconnect', { device_type: 'audio_input', disconnected_label: removedAudioIn.find(d => d.deviceId === selectedAudioInputDevice)?.label, fallback_label: fallback.label });
            pendingSwitches.push({
              deviceType: "microphone",
              deviceLabel: fallback.label,
            });
          } catch (e) {
            console.warn("[useDeviceHotPlug] Audio input fallback failed:", e);
          }
        } else if (addedAudioIn.length > 0 && !selectedAudioInDisappeared) {
          const newDevice = addedAudioIn[0];
          const previousDeviceId = selectedAudioInputDevice;
          try {
            await selectAudioInputDevice(newDevice.deviceId);
            await meetingManager.startAudioInputDevice(newDevice.deviceId);
            logDeviceEvent('device_hotplug_connect', { device_type: 'audio_input', new_device_label: newDevice.label });
            // Suppress the auto-switch toast when the new device matches the
            // Continuity rule — useIPhoneMicAlert will surface the dedicated
            // iPhone-mic toast for that case. The auto-switch itself proceeds
            // unchanged, which is what makes the new device the active mic
            // and triggers the iPhone-mic alert downstream.
            if (!isContinuityMicLabel(newDevice.label)) {
              pendingSwitches.push({
                deviceType: "microphone",
                deviceLabel: newDevice.label,
                revert: previousDeviceId
                  ? async () => {
                      try {
                        await selectAudioInputDevice(previousDeviceId);
                        await meetingManager.startAudioInputDevice(
                          previousDeviceId,
                        );
                        logDeviceEvent('device_hotplug_undo', { device_type: 'audio_input', reverted_to: previousDeviceId });
                      } catch {
                        /* device may no longer exist */
                      }
                    }
                  : undefined,
              });
            }
          } catch (e) {
            console.warn("[useDeviceHotPlug] Audio input switch failed:", e);
          }
        }

        // --- Audio Output ---
        const supportsSetSinkId =
          new DefaultBrowserBehavior().supportsSetSinkId();

        if (supportsSetSinkId) {
          const prevAudioOut = prevAudioOutputRef.current;
          const addedAudioOut = audioOutputDevices.filter(
            (d) => !prevAudioOut.find((p) => p.deviceId === d.deviceId),
          );
          const removedAudioOut = prevAudioOut.filter(
            (p) => !audioOutputDevices.find((d) => d.deviceId === p.deviceId),
          );
          const selectedAudioOutDisappeared = removedAudioOut.some(
            (d) => d.deviceId === selectedAudioOutputDevice,
          );

          if (
            selectedAudioOutDisappeared &&
            audioOutputDevices.length > 0
          ) {
            const fallback = audioOutputDevices[0];
            try {
              await selectAudioOutputDevice(fallback.deviceId);
              await meetingManager.startAudioOutputDevice(fallback.deviceId);
              logDeviceEvent('device_hotplug_disconnect', { device_type: 'audio_output', disconnected_label: removedAudioOut.find(d => d.deviceId === selectedAudioOutputDevice)?.label, fallback_label: fallback.label });
              pendingSwitches.push({
                deviceType: "speaker",
                deviceLabel: fallback.label,
              });
            } catch (e) {
              console.warn(
                "[useDeviceHotPlug] Audio output fallback failed:",
                e,
              );
            }
          } else if (
            addedAudioOut.length > 0 &&
            !selectedAudioOutDisappeared
          ) {
            const newDevice = addedAudioOut[0];
            const previousDeviceId = selectedAudioOutputDevice;
            try {
              await selectAudioOutputDevice(newDevice.deviceId);
              await meetingManager.startAudioOutputDevice(newDevice.deviceId);
              logDeviceEvent('device_hotplug_connect', { device_type: 'audio_output', new_device_label: newDevice.label });
              pendingSwitches.push({
                deviceType: "speaker",
                deviceLabel: newDevice.label,
                revert: previousDeviceId
                  ? async () => {
                      try {
                        await selectAudioOutputDevice(previousDeviceId);
                        await meetingManager.startAudioOutputDevice(
                          previousDeviceId,
                        );
                        logDeviceEvent('device_hotplug_undo', { device_type: 'audio_output', reverted_to: previousDeviceId });
                      } catch {
                        /* device may no longer exist */
                      }
                    }
                  : undefined,
              });
            } catch (e) {
              console.warn(
                "[useDeviceHotPlug] Audio output switch failed:",
                e,
              );
            }
          }
        }

        // --- Video Input (fallback only, no auto-switch on connect) ---
        const removedVideo = prevVideoInputRef.current.filter(
          (p) => !videoInputDevices.find((d) => d.deviceId === p.deviceId),
        );
        const selectedVideoDisappeared = removedVideo.some(
          (d) => d.deviceId === selectedVideoInputDevice,
        );

        if (selectedVideoDisappeared && videoInputDevices.length > 0) {
          const fallback = videoInputDevices[0];
          try {
            await selectVideoInputDevice(fallback.deviceId);
            const localTile = audioVideo.getLocalVideoTile();
            if (localTile?.state()?.active) {
              await meetingManager.startVideoInputDevice(fallback.deviceId);
            }
            logDeviceEvent('device_hotplug_disconnect', { device_type: 'video_input', disconnected_label: removedVideo.find(d => d.deviceId === selectedVideoInputDevice)?.label, fallback_label: fallback.label });
            pendingSwitches.push({
              deviceType: "camera",
              deviceLabel: fallback.label,
            });
          } catch (e) {
            console.warn(
              "[useDeviceHotPlug] Video input fallback failed:",
              e,
            );
          }
        }

        flushPendingSwitches(pendingSwitches);

        // Update refs
        prevAudioInputRef.current = audioInputDevices;
        prevAudioOutputRef.current = audioOutputDevices;
        prevVideoInputRef.current = videoInputDevices;
      })();
    }, 500);

    return () => clearTimeout(timer);
  }, [
    audioInputDevices,
    audioOutputDevices,
    videoInputDevices,
    audioVideo,
    selectedAudioInputDevice,
    selectedAudioOutputDevice,
    selectedVideoInputDevice,
    selectAudioInputDevice,
    selectAudioOutputDevice,
    selectVideoInputDevice,
    meetingManager,
  ]);
}
